import {create} from 'zustand';
import {List} from 'immutable';
import {immer} from 'zustand/middleware/immer';
import {devtools} from 'zustand/middleware';
import {genericMandarkRequest} from 'resources/mandark.resource';
import {query} from '@albert-io/json-api-framework/request/builder';
import {addToast} from '@albert-io/atomic';
import notifier from '@albert-io/notifier';
import errorCodes from 'client/errorCodes';
import {ERROR_TYPES} from 'client/constants';
import {replaceQueryParams} from 'client/history';

import type {ClassroomModelV1, SubjectModelV2, StudentModelV2} from '@albert-io/models';

import {
  getClassroomInvitationsQuery as getInvitationsQuery,
  getClassroomsQuery,
  getIndiePracticeSubjectsQuery as getSubjectsQuery,
  getStudentQuery
} from './StudentDashboard.queries';

type State = {
  classrooms: List<ClassroomModelV1>;
  duplicateClassroom: ClassroomModelV1 | null;
  pastDueAssignmentsCount: number;
  isReady: boolean;
  isRequestPending: boolean;
  student: StudentModelV2 | null;
  studentHasInvalidEmail: boolean;
  exceededHardCapLimit: boolean;
  subjects: List<SubjectModelV2>;
  totalPages: number;
  page: number;
  // TODO: type these
  openAssignments: List<any>;
  pastDueAssignments: List<any>;
  invitations: List<any>;
};

type Payload = {
  relationship: any;
  onReject: (err?: any) => void;
  onResolve: () => void;
};

type Actions = {
  clearDashboardCache: (page: number) => void;
  enrollStudent: (payload: Payload) => void;
  bootstrapStudentDashboard: (pageOverride?: number, classroomId?: string) => void;
  setHasInvalidEmail: (hasInvalidEmail: State['studentHasInvalidEmail']) => void;
  setExceededHardCapLimit: (hardCapExceeded: State['exceededHardCapLimit']) => void;
  setDuplicateClassroom: (duplicateClassroom: State['duplicateClassroom']) => void;
  setPage: (page: State['page']) => void;
};

const initialState: State = {
  classrooms: List([]),
  duplicateClassroom: null,
  pastDueAssignmentsCount: 0,
  isReady: false,
  isRequestPending: false,
  student: null,
  studentHasInvalidEmail: false,
  exceededHardCapLimit: false,
  subjects: List([]),
  totalPages: 1,
  openAssignments: List([]),
  pastDueAssignments: List([]),
  invitations: List([]),
  page: 1
};

const PAGE_SIZE = 6;

export const useStudentDashboard = create<State & Actions>()(
  devtools(
    immer((set, get) => ({
      ...initialState,
      clearDashboardCache: (page: number) => {
        const {student} = get();
        if (student) {
          const id = student.getId();
          const email = student.getEmail();
          getInvitationsQuery(email).invalidateInterest();
          getClassroomsQuery(id).page(page).pageSize(PAGE_SIZE).invalidateInterest();
          getSubjectsQuery().invalidateInterest();
          getStudentQuery().invalidateInterest();
        }
      },
      setHasInvalidEmail: (hasInvalidEmail: State['studentHasInvalidEmail']) => {
        set((state) => {
          state.studentHasInvalidEmail = hasInvalidEmail;
        });
      },
      setExceededHardCapLimit: (hardCapExceeded: State['exceededHardCapLimit']) => {
        set((state) => {
          state.exceededHardCapLimit = hardCapExceeded;
        });
      },
      setDuplicateClassroom: (duplicateClassroom: State['duplicateClassroom']) => {
        set((state) => {
          state.duplicateClassroom = duplicateClassroom;
        });
      },
      setPage: (page: State['page']) => {
        set((state) => {
          state.page = page;
        });
      },
      enrollStudent: async ({relationship = {}, onResolve = () => {}, onReject = () => {}}) => {
        const {
          student,
          clearDashboardCache,
          setHasInvalidEmail,
          setExceededHardCapLimit,
          page,
          bootstrapStudentDashboard
        } = get();
        if (student) {
          try {
            const id = student.getId();
            await genericMandarkRequest(
              'post',
              query().mandarkEndpoint(['students_v2', id, 'relationships', 'classrooms_v1']).done(),
              {
                data: [relationship]
              }
            );
            clearDashboardCache(page);
            addToast({
              duration: 10000,
              message: 'You have joined a new class',
              title: 'Success!',
              color: 'positive'
            });
            onResolve();
            set((state) => {
              state.isReady = true;
            });
            // replicates old behavior of reloading entire dashboard // https://github.com/albert-io/nexus/issues/13443#issuecomment-3374784269
            bootstrapStudentDashboard(page);
          } catch (err: any) {
            const exceededHardCapLimitError =
              err.response?.status === 403 &&
              err.response?.body.errors[0].code === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT;
            if (exceededHardCapLimitError) {
              setExceededHardCapLimit(true);
            }
            // don't notify for 400 errors (to suppress invalid join code)
            if (err.response?.status !== 400) {
              notifier.notify(err, {
                message: 'Failed to enroll student using relationship',
                tags: 'StudentDashboardProvider',
                context: {
                  relationship: relationship
                }
              });
            }
            /* Catch the Errors.BadRequest.InvalidEmailDomain here to cover join code, email link, and invitation card enrollment flows */
            const invalidEmail =
              err.response?.status === 400 &&
              err.response?.body.errors[0].code ===
                errorCodes.BAD_REQUEST.VALIDATION_ERROR.INVALID_EMAIL_DOMAIN;
            if (invalidEmail) {
              setHasInvalidEmail(true);
            }
            onReject(err);
          }
        }
      },
      bootstrapStudentDashboard: async (page, classroomId) => {
        const shouldEnrollStudentByParam = Boolean(classroomId);
        try {
          set((state) => {
            state.isReady = false;
          });
          const fetchedStudent = await getStudentQuery().getResourcePromise();
          const studentId = fetchedStudent.getId();
          const email = fetchedStudent.getEmail();

          const paginatedClassroomsQuery = getClassroomsQuery(studentId)
            .page(page)
            .pageSize(PAGE_SIZE);
          const fetchedClassrooms = await paginatedClassroomsQuery.getResourcePromise();

          const fetchedInvitations = await getInvitationsQuery(email).getResourcePromise();
          // @todo Refactor getClassroomInvitationsQuery with the query params that will achieve this same behavior
          const filteredInvitations = fetchedInvitations.filter(
            (invitation) =>
              !invitation.getClassroom().isEmpty() && !invitation.getIssuer().isEmpty()
          );

          const fetchedSubjects = await getSubjectsQuery().getResourcePromise();

          const assignmentInfo = fetchedStudent?.getMeta?.()?.getAssignmentInfo?.();
          const fetchedOpenAssignments = assignmentInfo?.get?.('open_assignments');
          const fetchedPastDueAssignments = assignmentInfo?.get?.('past_due_assignments');
          const fetchedPastDueAssignmentsCount = assignmentInfo?.get?.(
            'past_due_assignments_count'
          );
          const totalPages = paginatedClassroomsQuery
            .getResourceMetadata()
            .getIn(['page', 'total_pages'], 1);

          set((state) => {
            state.openAssignments = fetchedOpenAssignments;
            state.pastDueAssignments = fetchedPastDueAssignments;
            state.pastDueAssignmentsCount = fetchedPastDueAssignmentsCount;
            state.classrooms = fetchedClassrooms;
            state.invitations = filteredInvitations;
            state.student = fetchedStudent;
            state.subjects = fetchedSubjects;
            state.totalPages = totalPages;
            state.isReady = shouldEnrollStudentByParam ? false : true;
          });
        } catch (err) {
          addToast({
            title: 'Something went wrong',
            color: 'negative',
            message:
              'There was a problem loading your classrooms. If the problem persists, contact us.'
          });
          history.pushState(null, `/error?errorCode=${ERROR_TYPES.CRITICAL}`);
        }

        if (shouldEnrollStudentByParam) {
          const {enrollStudent, bootstrapStudentDashboard} = get();
          const relationship = {
            id: classroomId,
            type: 'classroom_v1'
          };
          await enrollStudent({
            relationship,
            onResolve: () => {
              replaceQueryParams({classroomId: null, classroomInvitationId: null});
              // replicates old behavior of reloading entire dashboard. // https://github.com/albert-io/nexus/issues/13443#issuecomment-3374784269
              bootstrapStudentDashboard(page);
            },
            onReject: () => {
              set((state) => {
                state.isReady = true;
              });
              addToast({
                title: 'Something went wrong',
                color: 'negative',
                message:
                  'We were unable to complete your enrollment. Please verify the invitation with your teacher and try again.'
              });
            }
          });
        }
      }
    }))
  )
);
